import React, { useState, useMemo } from 'react';

interface NotificationSection {
  sectionTitle: string;
  items: string[];
}

interface Notification {
  _id: string;
  title: string;
  description: string;
  version: string;
  category: 'bug-fix' | 'feature' | 'security' | 'improvement' | 'maintenance';
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'draft' | 'published' | 'archived';
  featured: boolean;
  bannerImage?: string;
  sections?: NotificationSection[];
  tags?: string[];
  releaseDate: string | Date;
  createdAt: string | Date;
  updatedAt: string | Date;
}

interface NotificationCardProps {
  notification: Notification;
  featured?: boolean;
  theme?: {
    color1?: string;
    color2?: string;
    txtColor?: string;
  };
}

const NotificationCard: React.FC<NotificationCardProps> = ({ notification, featured = false, theme }) => {
  const [imageError, setImageError] = useState(false);

  // Generate unique wave pattern for each card
  const wavePattern = useMemo(() => {
    const id = notification._id || Math.random().toString(36).substr(2, 9);
    const hash = id.split('').reduce((a: number, b: string) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    // Different wave patterns
    const patterns = [
      // Pattern 1: Concentric circles
      `<svg width="100%" height="100%" viewBox="0 0 200 200" preserveAspectRatio="xMidYMid slice" class="absolute inset-0 opacity-20">
        <circle cx="100" cy="100" r="80" fill="none" stroke="rgba(0,0,0,0.15)" stroke-width="1"/>
        <circle cx="100" cy="100" r="60" fill="none" stroke="rgba(0,0,0,0.12)" stroke-width="1"/>
        <circle cx="100" cy="100" r="40" fill="none" stroke="rgba(0,0,0,0.1)" stroke-width="1"/>
        <circle cx="100" cy="100" r="20" fill="none" stroke="rgba(0,0,0,0.08)" stroke-width="1"/>
      </svg>`,
      
      // Pattern 2: Spiral
      `<svg width="100%" height="100%" viewBox="0 0 200 200" preserveAspectRatio="xMidYMid slice" class="absolute inset-0 opacity-20">
        <path d="M100,100 Q120,80 140,100 T180,100 Q160,120 140,100 T100,100 Q80,80 60,100 T20,100 Q40,120 60,100 T100,100" 
              fill="none" stroke="rgba(0,0,0,0.12)" stroke-width="1"/>
      </svg>`,
      
      // Pattern 3: Wave lines
      `<svg width="100%" height="100%" viewBox="0 0 200 200" preserveAspectRatio="xMidYMid slice" class="absolute inset-0 opacity-20">
        <path d="M0,50 Q50,30 100,50 T200,50" fill="none" stroke="rgba(0,0,0,0.1)" stroke-width="1"/>
        <path d="M0,100 Q50,80 100,100 T200,100" fill="none" stroke="rgba(0,0,0,0.12)" stroke-width="1"/>
        <path d="M0,150 Q50,130 100,150 T200,150" fill="none" stroke="rgba(0,0,0,0.1)" stroke-width="1"/>
      </svg>`,
      
      // Pattern 4: Diagonal lines
      `<svg width="100%" height="100%" viewBox="0 0 200 200" preserveAspectRatio="xMidYMid slice" class="absolute inset-0 opacity-20">
        <line x1="0" y1="0" x2="200" y2="200" stroke="rgba(0,0,0,0.08)" stroke-width="1"/>
        <line x1="0" y1="50" x2="200" y2="150" stroke="rgba(0,0,0,0.1)" stroke-width="1"/>
        <line x1="0" y1="100" x2="200" y2="100" stroke="rgba(0,0,0,0.09)" stroke-width="1"/>
        <line x1="0" y1="150" x2="200" y2="50" stroke="rgba(0,0,0,0.08)" stroke-width="1"/>
      </svg>`,
      
      // Pattern 5: Hexagonal pattern
      `<svg width="100%" height="100%" viewBox="0 0 200 200" preserveAspectRatio="xMidYMid slice" class="absolute inset-0 opacity-20">
        <polygon points="100,20 120,40 120,60 100,80 80,60 80,40" fill="none" stroke="rgba(0,0,0,0.1)" stroke-width="1"/>
        <polygon points="100,60 120,80 120,100 100,120 80,100 80,80" fill="none" stroke="rgba(0,0,0,0.12)" stroke-width="1"/>
        <polygon points="100,100 120,120 120,140 100,160 80,140 80,120" fill="none" stroke="rgba(0,0,0,0.1)" stroke-width="1"/>
      </svg>`
    ];
    
    return patterns[Math.abs(hash) % patterns.length];
  }, [notification._id]);


  const formatCategory = (category: string) => {
    const categoryMap: Record<string, string> = {
      'bug-fix': 'Bug Fix',
      'feature': 'Feature',
      'security': 'Security',
      'improvement': 'Improvement',
      'maintenance': 'Maintenance'
    };
    return categoryMap[category] || category;
  };

  const formatDate = (date: string | Date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const CategoryIcon: React.FC<{ category: string }> = ({ category }) => {
    const iconMap: Record<string, JSX.Element> = {
      'bug-fix': (
        <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      ),
      'feature': (
        <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" clipRule="evenodd" />
        </svg>
      ),
      'security': (
        <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      ),
      'improvement': (
        <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      ),
      'maintenance': (
        <svg className="w-4 h-4 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
        </svg>
      )
    };
    return iconMap[category] || iconMap['improvement'];
  };

  const PriorityBadge: React.FC<{ priority: string }> = ({ priority }) => {
    const priorityStyles: Record<string, string> = {
      'low': 'bg-gray-500 text-white',
      'medium': 'bg-blue-500 text-white',
      'high': 'bg-orange-500 text-white',
      'critical': 'bg-red-500 text-white'
    };
    
    return (
      <span className={`badge badge-sm text-xs font-medium border-0 ${priorityStyles[priority] || priorityStyles['medium']}`}>
        {priority.toUpperCase()}
      </span>
    );
  };

  return (
    <div
      className={`card notification-card shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-100 hover:scale-[1.02] relative overflow-hidden bg-white rounded-xl ${
        featured ? 'ring-2 ring-opacity-50' : ''
      }`}
      style={featured ? { 
        '--tw-ring-color': theme?.color2 || '#E9B54E'
      } as React.CSSProperties : {}}
    >
      {/* Header Section with Theme Color */}
      <div 
        className="p-4 rounded-t-xl"
        style={{
          background: theme?.color1 || '#1A2347',
          color: theme?.txtColor || '#ffffff'
        }}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {featured && <div className="w-3 h-3 rounded-full animate-pulse" style={{ backgroundColor: theme?.color2 || '#E9B54E' }}></div>}
            <div className="flex items-center gap-2">
              <CategoryIcon category={notification.category} />
              <span className="text-sm font-medium">{formatCategory(notification.category)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs px-2 py-1 rounded" style={{ 
              backgroundColor: theme?.color2 || '#E9B54E',
              color: theme?.txtColor || '#ffffff'
            }}>
              {notification.version}
            </span>
            <PriorityBadge priority={notification.priority} />
          </div>
        </div>
        
        {/* Title in Header */}
        <h3 className="font-bold text-lg mt-3 mb-2 line-clamp-2">
          {notification.title}
        </h3>
      </div>
      
      {/* Main Content Area - White Background */}
      <div className="bg-white relative">
        {/* Wave Pattern Background */}
        <div 
          className="absolute inset-0 pointer-events-none"
          dangerouslySetInnerHTML={{ __html: wavePattern }}
        />
        
        {/* Description Section */}
        {notification.description && (
          <div className="p-6 pb-4 relative z-10">
            <p className="text-sm text-black line-clamp-3">
              {notification.description}
            </p>
          </div>
        )}

        {/* Banner Image Section */}
        {notification.bannerImage && !imageError && (
          <div className="px-6 pb-4 relative z-10">
            <img 
              src={notification.bannerImage} 
              alt={notification.title}
              className="w-full h-32 object-cover rounded-lg"
              onError={handleImageError}
            />
          </div>
        )}

        {/* Update Sections Section */}
        {notification.sections && notification.sections.length > 0 && (
          <div className="px-6 pb-4 relative z-10">
            <div className="space-y-4">
              {notification.sections.slice(0, 2).map((section, index) => (
                <div key={index} className="border-l-2 border-gray-200 pl-4 py-2">
                  <h4 className="font-bold text-sm text-black mb-2 underline">
                    {section.sectionTitle}
                  </h4>
                  <ul className="text-sm text-black space-y-2">
                    {section.items.slice(0, 3).map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-center gap-3">
                        <span className="font-bold text-lg flex-shrink-0" style={{ color: theme?.color1 || '#1A2347' }}>•</span>
                        <span className="font-medium text-left">{item}</span>
                      </li>
                    ))}
                    {section.items.length > 3 && (
                      <li className="text-gray-500 italic text-xs ml-6">
                        +{section.items.length - 3} more items...
                      </li>
                    )}
                  </ul>
                </div>
              ))}
              {notification.sections.length > 2 && (
                <div className="text-xs text-gray-500 italic text-center py-2">
                  +{notification.sections.length - 2} more sections...
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tags Section */}
        {notification.tags && notification.tags.length > 0 && (
          <div className="px-6 pb-4 relative z-10">
            <div className="flex flex-wrap gap-2">
              {notification.tags.slice(0, 3).map((tag, index) => (
                <span 
                  key={index}
                  className="text-xs font-bold py-1 px-2 rounded-md"
                  style={{ backgroundColor: theme?.color2 || '#E9B54E', color: theme?.txtColor || '#ffffff' }}
                >
                  {tag}
                </span>
              ))}
              {notification.tags.length > 3 && (
                <span 
                  className="text-xs font-bold py-1 px-2 rounded-md"
                  style={{ backgroundColor: theme?.color2 || '#E9B54E', color: theme?.txtColor || '#ffffff' }}
                >
                  +{notification.tags.length - 3}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Footer Section */}
        <div 
          className="px-6 py-4 border-t border-gray-100 relative z-10 rounded-b-xl"
          style={{
            background: theme?.color1 || '#1A2347',
            color: theme?.txtColor || '#ffffff'
          }}
        >
          <div className="flex items-center gap-2 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>{formatDate(notification.releaseDate)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCard;
